'use client';

import { CinemarkLink } from '@/components/cinemark-link/cinemark-link';
import { Card, CardContent } from '@/components/ui/card';
import { AnalyticsEvent, AnalyticsLinkAction } from '@/enums/analytics-type';
import { useDataAnalytics } from '@/hooks/analytics/analytics';
import { BasicShowtime, ShowtimeStatusType } from '@/types/showtimes';
import { formatDateTimeWithoutOffset } from '@/utils/date-time-helper';
import { formatShowtimeTimeOnly } from '@/utils/format-helpers';
import clsx from 'clsx';
import { useState } from 'react';

type ShowtimeComponentProps = {
  cinemarkMovieId: number;
  dataLinkCategory: string;
  forceChipStyle?: boolean;
  showtime: BasicShowtime;
  theaterId: number;
  timeZone?: string;
};

const fullDateFormatterCache = new Map<string, Intl.DateTimeFormat>();

const formatShowtimeFullDate = (date: Date, timeZone?: string): string => {
  const key = timeZone ?? 'local';
  let formatter = fullDateFormatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      day: 'numeric',
      month: 'long',
      timeZone,
      weekday: 'long',
      year: 'numeric',
    });
    fullDateFormatterCache.set(key, formatter);
  }
  return formatter.format(date);
};

const buildShowtimeAriaLabel = (
  showtime: BasicShowtime,
  showtimeTime: string,
  timeZone: string | undefined
): string => {
  if (showtime.status === ShowtimeStatusType.SoldOut) {
    return `${showtimeTime} showtime is sold out`;
  }
  if (showtime.status === ShowtimeStatusType.PreSaleOnly) {
    return `${showtimeTime} showtime is presale only`;
  }
  const fullDate = formatShowtimeFullDate(showtime.startTime, timeZone);
  return `Select ${showtimeTime} showtime for ${fullDate}`;
};

export const ShowtimeComponent = ({
  cinemarkMovieId,
  dataLinkCategory,
  forceChipStyle = false,
  showtime,
  theaterId,
  timeZone,
}: ShowtimeComponentProps): React.JSX.Element | null => {
  const [isHovered, setIsHovered] = useState(false);
  const { trackEvent } = useDataAnalytics();

  if (!showtime) return null;

  const query = new URLSearchParams({
    cinemarkMovieId: cinemarkMovieId.toString(),
    linkedShowtimeId: showtime.linkedShowtimeId?.toString() ?? '',
    showtime: formatDateTimeWithoutOffset(showtime.startTime, timeZone),
    showtimeId: showtime.id.toString(),
    theaterId: theaterId.toString(),
  });
  const showtimeUrl = showtime.isPrivateWatchParty
    ? `/PrivateWatchParty/?${query.toString()}`
    : `/TicketSeatMap/?${query.toString()}`;
  const showtimeTime = formatShowtimeTimeOnly(showtime.startTime, timeZone);
  const isPresale = showtime.status === ShowtimeStatusType.PreSaleOnly;
  const isSoldOut = showtime.status === ShowtimeStatusType.SoldOut;
  const isDisabled = isSoldOut || isPresale;
  const ariaLabel = buildShowtimeAriaLabel(showtime, showtimeTime, timeZone);
  const interactiveClassName = isDisabled
    ? isSoldOut ? 'opacity-50' : ''
    : 'cursor-pointer hover:opacity-80';

  const handleMouseEnter = (): void => {
    setIsHovered(true);
  };

  const handleMouseLeave = (): void => {
    setIsHovered(false);
  };

  const getDisplayText = (): string => {
    switch (showtime.status) {
      case ShowtimeStatusType.Saleable:
      case ShowtimeStatusType.PreSaleOnly:
        return showtimeTime;
      case ShowtimeStatusType.SoldOut:
        return isHovered ? 'Sold Out' : showtimeTime;
      default:
        return showtimeTime;
    }
  };

  const renderContent =
    <Card fullHeader
      aria-disabled={isDisabled ? true : undefined}
      aria-label={isDisabled ? ariaLabel : undefined}
      className={clsx(
        'block w-20 px-xs py-sm rounded-lg text-center transition-opacity',
        isPresale && !forceChipStyle
          ? 'bg-transparent border-transparent shadow-none'
          : interactiveClassName
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <CardContent>
        <p className="text-sm font-medium"
          data-test="showtime-text"
        >
          {getDisplayText()}
        </p>
      </CardContent>
    </Card>
  ;

  if (isDisabled) {
    return renderContent;
  }

  return (
    <CinemarkLink disableAutoTracking
      useAnchor
      aria-label={ariaLabel}
      className="inline-block"
      dataTest={`link-showtime-id-${showtime.id}`}
      href={showtimeUrl}
      onClick={() => {
        trackEvent(AnalyticsEvent.SELECT_SHOWTIME, {
          linkAction: AnalyticsLinkAction.CLICK,
          linkCategory: dataLinkCategory,
          linkName: 'Showtime Selected',
        });
      }}
    >
      {renderContent}
    </CinemarkLink>
  );
};
